/**
 * VerticalContext — marca dinámica del panel según la vertical de producto (NER-4044).
 *
 * Consultame (NER-3656/NER-3832) reutiliza el admin de Citame con `?vertical=medical`
 * (Opción A del split de producto). El param llega desde el CTA del landing
 * Consultame y se propaga tras el signup (MedicalSetupWizard → `/?org=...&vertical=medical`).
 *
 * Este contexto resuelve la confusión UX reportada por Diego: el usuario médico
 * clickea "Consultame" pero el panel decía "Citame". Acá cambiamos SOLO la MARCA
 * PERCIBIDA (logo, título de pestaña, paleta) — la URL sigue siendo
 * `/citame/admin/...` porque el build está horneado a `homepage:/citame/admin`
 * (cambiar la URL NO es zero-infra; requiere CloudFront behavior + rewrite +
 * basename runtime — ver NER-4044, decisión Diego pendiente).
 *
 * Persistimos la vertical en sessionStorage (por pestaña) para que el chrome se
 * mantenga Consultame aunque una navegación client-side pierda el query param
 * (ej. paso de confirmación de email que recarga sin el param).
 */
import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';

const VerticalContext = createContext({ vertical: null, isMedical: false });

const STORAGE_KEY = 'admin_vertical';
const VALID_VERTICALS = ['medical'];

function readVerticalFromUrl() {
  try {
    // Detección por PATH primero (NER-4047): Consultame se sirve bajo
    // `/consultame/admin` (CloudFront behavior dedicado — NER-4046) o con
    // `vertical=medical` embebido en la ruta. Cualquiera activa la vertical
    // médica sin depender del query param, que puede perderse en navegación.
    const { pathname, search } = window.location;
    if (pathname.startsWith('/consultame/admin') || pathname.includes('vertical=medical')) {
      return 'medical';
    }
    // Fallback: query param `?vertical=medical` (CTA del landing Consultame +
    // redirect post-signup del MedicalSetupWizard).
    const v = new URLSearchParams(search).get('vertical');
    return v && VALID_VERTICALS.includes(v) ? v : null;
  } catch (_e) {
    return null;
  }
}

// URL manda; si no hay param, caemos al valor persistido en la sesión.
function resolveInitialVertical() {
  const fromUrl = readVerticalFromUrl();
  if (fromUrl) {
    try {
      window.sessionStorage.setItem(STORAGE_KEY, fromUrl);
    } catch (_e) {
      /* modo privado / storage bloqueado — degradá a solo-URL */
    }
    return fromUrl;
  }
  try {
    const stored = window.sessionStorage.getItem(STORAGE_KEY);
    return stored && VALID_VERTICALS.includes(stored) ? stored : null;
  } catch (_e) {
    return null;
  }
}

export function VerticalProvider({ children }) {
  const [vertical] = useState(resolveInitialVertical);
  const isMedical = vertical === 'medical';

  // Efecto de chrome global: atributo en <html> (activa la paleta CSS de
  // Consultame en index.css) + título de la pestaña.
  useEffect(() => {
    const root = document.documentElement;
    if (vertical) {
      root.setAttribute('data-vertical', vertical);
    } else {
      root.removeAttribute('data-vertical');
    }
    if (isMedical) {
      document.title = 'Consultame Admin';
    }
  }, [vertical, isMedical]);

  const value = useMemo(() => ({ vertical, isMedical }), [vertical, isMedical]);

  return <VerticalContext.Provider value={value}>{children}</VerticalContext.Provider>;
}

export function useVertical() {
  return useContext(VerticalContext);
}

export default VerticalContext;
